import type { GetError } from "@dealer-center/core-components";
import {
  Alert,
  Button,
  SelectInput,
  TextInput
} from "@dealer-center/core-components";
import type { GroupsPayload } from "@dealer-center/ddc-pages-incentives-manager-lambda";
import "./GroupSubpanel.scss";
import { Locale } from "@dealer-center/ddc-core-utils";
import { useState } from "react";
import { useWriteAPI } from "@dealer-center/ddc-pages-incentives-manager-fetch";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import type { decodedJwtAndPermissions } from "@dealer-center/ddc-api-user-interface";
import { useOutletContext } from "react-router-dom";
import { containsProhibitedContent } from "../utils/validation";

interface GroupSubpanelProps {
  groups: GroupsPayload;
  selectedGroup?: string;
  onGroupSelect?: (groupId: string) => void;
  onGroupCreate?: (groups: GroupsPayload) => void;
  onGroupDelete?: (groups: GroupsPayload) => void;
}

export const buildGroupOptions = (groups: GroupsPayload) => {
  return Object.values(groups).map(group => ({
    label: group.name || "",
    value: group.id,
    disabled: false
  }));
};

export const addNewGroup = (groupName: string, groups: GroupsPayload) => {
  const clonedGroups = JSON.parse(JSON.stringify(groups));
  clonedGroups.push({
    id: `group-${Date.now()}`, // Generate a unique ID for the new group
    name: groupName,
    members: [],
    owner: {
      ownerId: "",
      type: ""
    }
  });
  return clonedGroups;
};

export const deleteGroup = (groupId: string, groups: GroupsPayload) => {
  const clonedGroups = JSON.parse(JSON.stringify(groups));
  const groupIndex = clonedGroups.findIndex(group => group.id === groupId);
  if (groupIndex !== -1) {
    clonedGroups.splice(groupIndex, 1);
  }
  return clonedGroups;
};

export function validateName(groups: GroupsPayload): GetError | undefined {
  return (_label, value) => {
    if (!value) {
      return {
        triggered: false,
        validationState: {
          isValid: true,
          type: "success"
        }
      };
    }
    const v = value?.trim() || "";
    if (v.length === 0) {
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: Locale.getText("BLANK_NAME") as string
        }
      };
    }
    if (containsProhibitedContent(v)) {
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: Locale.getText("PROHIBITED_CONTENT_ERROR_MESSAGE") as string
        }
      };
    }
    if (groups.some(group => group.name?.toUpperCase() === v.toUpperCase())) {
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: Locale.getText("NAME_ALREADY_EXISTS") as string
        }
      };
    }
    return {
      triggered: false,
      validationState: {
        isValid: true,
        type: "success"
      }
    };
  };
}

export const GroupSubpanel = ({
  groups,
  selectedGroup,
  onGroupSelect,
  onGroupCreate,
  onGroupDelete
}: GroupSubpanelProps) => {
  const hasGroups = groups && Object.keys(groups).length > 0;
  const [adding, setAdding] = useState<boolean>(false);
  const [newGroupName, setNewGroupName] = useState<string>("");
  const [isWriting, setIsWriting] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const { ccUserInfo } = useOutletContext<{
    ccUserInfo: ControlcenterAPIInterfaceExternal;
    userEntity: decodedJwtAndPermissions;
  }>();
  const setMutateError = (label: string, error: Error) => {
    setError(Locale.getText(label) as string);
    console.warn(error.message); // eslint-disable-line no-console
  };
  const deleteGroupMutation = useWriteAPI({
    resource: "delete-distribution-lists-group",
    asAccount: ccUserInfo.accountId,
    asUser: ccUserInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    afterSuccess: "distribution-lists"
  });
  const createGroupMutation = useWriteAPI({
    resource: "create-distribution-lists-group",
    asAccount: ccUserInfo.accountId,
    asUser: ccUserInfo.userId,
    locale: Locale.getCurrent().replace("-", "_"),
    afterSuccess: "distribution-lists"
  });

  const renderAddingGroup = () => {
    return (
      <div className="add-group">
        <div className="add-group-field">
          <TextInput
            getError={validateName(groups)}
            label={Locale.getText("GROUP_NAME") as string}
            maxLength={30}
            name={"new-group-name"}
            value={""}
            onChange={event => {
              setNewGroupName(event.target.value);
            }}
          />
          {hasGroups && (
            <Button
              analyticsConfiguration={{
                eventName: "",
                eventProperties: undefined
              }}
              buttonStyle="secondary"
              onClick={() => {
                setAdding(false);
              }}
            >
              {Locale.getText("CANCEL_NEW_GROUP")}
            </Button>
          )}
        </div>

        <Button
          analyticsConfiguration={{
            eventName: "",
            eventProperties: undefined
          }}
          buttonStyle="primary"
          disabled={
            newGroupName.length === 0 ||
            containsProhibitedContent(newGroupName.trim())
          }
          isLoading={isWriting}
          onClick={async () => {
            setAdding(false);
            setIsWriting(true);
            try {
              const serverResponse = await createGroupMutation.mutateAsync({
                body: { name: newGroupName }
              });

              const clonedGroups = JSON.parse(JSON.stringify(groups));
              clonedGroups.push(serverResponse);

              onGroupCreate?.(clonedGroups);
              setNewGroupName("");
              setIsWriting(false);
            } catch (error) {
              setIsWriting(false);
              setMutateError("ERROR_CREATING_DISTRIBUTION_LISTS", error);
            }
          }}
        >
          {Locale.getText("CREATE")}
        </Button>
      </div>
    );
  };
  const renderConfirmDelete = () => {
    return (
      <div className="confirm-delete">
        <div className="confirm-delete-text">
          {Locale.getText("CONFIRM_DELETE_GROUP") as string}
        </div>
        <Button
          analyticsConfiguration={{
            eventName: "",
            eventProperties: undefined
          }}
          buttonStyle="secondary"
          onClick={async () => {
            setAdding(false);
            setIsWriting(true);
            setIsConfirming(false);
            try {
              await deleteGroupMutation.mutateAsync({
                resourceId: selectedGroup || ""
              });
              onGroupDelete?.(deleteGroup(selectedGroup || "", groups));
            } catch (error) {
              setMutateError("ERROR_DELETING_DISTRIBUTION_LISTS", error);
            } finally {
              setIsWriting(false);
            }
          }}
        >
          {Locale.getText("CONFIRM") as string}
        </Button>
        <Button
          analyticsConfiguration={{
            eventName: "",
            eventProperties: undefined
          }}
          buttonStyle="secondary"
          onClick={async () => {
            setIsConfirming(false);
          }}
        >
          {Locale.getText("CANCEL") as string}
        </Button>
      </div>
    );
  };
  const renderGroupList = () => {
    return (
      <div className={`group-list ${isWriting ? "disabled" : ""}`}>
        <div className={`group-list-selection`}>
          <SelectInput
            displayDeselectOption={false}
            id="for-pref-manage-groups"
            label={Locale.getText("MANAGE_GROUPS") as string}
            options={groups ? buildGroupOptions(groups) : []}
            value={selectedGroup}
            onChange={event => {
              onGroupSelect?.(event.target.value || "");
            }}
          />
          <Button
            analyticsConfiguration={{
              eventName: "",
              eventProperties: undefined
            }}
            buttonStyle="secondary"
            onClick={async () => {
              setIsConfirming(true);
            }}
          >
            {Locale.getText("DELETE")}
          </Button>
        </div>
        <div>
          <Button
            analyticsConfiguration={{
              eventName: "",
              eventProperties: undefined
            }}
            buttonStyle="secondary"
            onClick={() => {
              setAdding(true);
            }}
          >
            {Locale.getText("NEW_GROUP")}
          </Button>
        </div>
      </div>
    );
  };
  return (
    <div className="group-subpanel">
      {error && (
        <div className="group-subpanel-error">
          <Alert role="alert" sx={{ marginBottom: "16px" }} type="error">
            {error}
          </Alert>
        </div>
      )}
      {isConfirming
        ? renderConfirmDelete()
        : adding || !hasGroups
          ? renderAddingGroup()
          : renderGroupList()}
    </div>
  );
};
